import { experimental_extendTheme as extendTheme } from "@mui/material/styles";

import { ArrowDownIcon } from "../components/Icons/Icons";

const darkModePalette = {
  // Dark mode palette
  primary: {
    main: "#4e801f",
    dark: "#2b5f0e",
    light: "#80bc00",
    contrastText: "#DCDDDE",
  },
  secondary: {
    main: "#f58320",
    dark: "#e06627",
    light: "#eba900",
    contrastText: "#FFFFFF",
  },
  text: {
    primary: "#DCDDDE",
    secondary: "#b0b0b0",
    disabled: "#666666",
  },
  error: {
    main: "#f44336",
    dark: "#d32f2f",
    light: "#e57373",
  },
  warning: {
    main: "#ff9800",
    dark: "#f57c00",
    light: "#ffb74d",
  },
  info: {
    main: "#2196f3",
    dark: "#1976d2",
    light: "#64b5f6",
  },
  success: {
    main: "#4caf50",
    dark: "#388e3c",
    light: "#81c784",
  },
  background: {
    default: "#36393F",
    paper: "#40444B",
    table: "#40444B",
  },
  action: {
    focus: "#666666",
    hover: "#2a2a2a",
    selected: "#404040",
  },
  grey: {
    900: "#fafafa",
    800: "#f5f5f5",
    700: "#eeeeee",
    600: "#e0e0e0",
    500: "#bdbdbd",
    400: "#9e9e9e",
    300: "#757575",
    200: "#616161",
    100: "#4c5056",
    50: "#212121",
    A100: "#4c5056",
    A200: "#616161",
  },
  divider: "#c4c4c4",
  colors: {
    // ===== GRAY SCALE COLORS =====
    // Dark grays (darkest to lightest)
    borderNeutralDarkGray: "#666666",
    textGraySlate: "#9e9e9e",
    semiTransparentGray: "#80808075",
    borderVeryDarkGray: "#555555",
    borderLight: "#404040",
    borderVeryLight: "#2a2a2a",
    borderLightGray: "#333333",
    borderUltraLightGray: "#333333",

    // ===== PRIMARY COLORS (GREEN) =====
    // Primary green variations (darkest to lightest)
    backgroundSuccess: "#0a2a0a",
    backgroundNewRow: "#1a2a1a",
    textGreen: "#4caf50",
    textVibrantGreen: "#66bb6a",
    textDarkGreen: "#81c784",
    brandLightGreen: "#8fb954",

    // ===== SECONDARY COLORS (ORANGE/YELLOW) =====
    // Orange variations (darkest to lightest)
    backgroundWarning: "#2a1f0a",
    backgroundAmber: "#8a6600",
    textOrangeAccent: "#ff9800",
    textWarningYellow: "#ffb74d",
    brandMediumOrange: "#d2d550",

    // Blue variations (darkest to lightest)
    backgroundLightCyan: "#1a1f1f",
    textBlue: "#64b5f6",
    infoSkyBlue: "#64b5f6",
    textDarkBlue: "#90caf9",

    // Red variations (darkest to lightest)
    textDangerRed: "#f44336",
    alertRed: "#f44336",
    textAlertRed: "#ff5252",
    textRed: "#ff6b6b",

    // Other status colors (darkest to lightest)
    highlightLime: "#76ff03",

    // ===== CHART COLORS =====
    // Chart colors (darkest to lightest)
    chartTeal: "#4db6ac",
    chartBlue: "#64b5f6",
    chartCoralRed: "#e57373",
    chartOrange: "#ff8a65",
    chartYellowOrange: "#ffb74d",
    chartSlateGray: "#9e9e9e",
    chartViolet: "#ba68c8",

    // ===== UTILITY COLORS =====
    // Transparent and overlay colors
    transparent: "transparent",
  },
};

const lightModePalette = {
  common: {
    white: "#ffffff",
    black: "#000000",
  },
  primary: {
    main: "#4e801f",
    dark: "#2b5f0e",
    light: "#80bc00",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#f58320",
    dark: "#e06627",
    light: "#eba900",
    contrastText: "#ffffff",
  },
  text: {
    primary: "#000000",
    secondary: "#666666",
    disabled: "#9e9e9e",
  },
  error: {
    main: "#d32f2f",
    dark: "#c62828",
    light: "#ef5350",
  },
  warning: {
    main: "#ef6c00",
    dark: "#e65100",
    light: "#ff9800",
  },
  info: {
    main: "#0288d1",
    dark: "#01579b",
    light: "#03a9f4",
  },
  success: {
    main: "#2e7d32",
    dark: "#1b5e20",
    light: "#4caf50",
  },
  background: {
    default: "#f0ece6",
    paper: "#ffffff",
    table: "#f0efeb",
  },
  action: {
    focus: "#a6a6a6",
    hover: "#f6f6f6",
    selected: "#e3e3e3",
  },
  grey: {
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#eeeeee",
    300: "#e0e0e0",
    400: "#bdbdbd",
    500: "#9e9e9e",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
    A100: "#f5f5f5",
    A200: "#eeeeee",
  },
  divider: "#c4c4c4",
  colors: {
    // ===== GRAY SCALE COLORS =====
    // Dark grays (darkest to lightest)
    borderNeutralDarkGray: "#B5B5B5",
    textGraySlate: "#7F8C8D",
    semiTransparentGray: "#80808075",
    borderVeryDarkGray: "#a6a6a6",
    borderLight: "#c0c0c0",
    borderVeryLight: "#dddddd",
    borderLightGray: "#E9E9E9",
    borderUltraLightGray: "#E9E9E9",

    // ===== PRIMARY COLORS (GREEN) =====
    // Primary green variations (darkest to lightest)
    textDarkGreen: "#4F9C06",
    textGreen: "#008000",
    textVibrantGreen: "#2ECC71",
    brandLightGreen: "#8fb954",
    backgroundNewRow: "#e6ffe6",
    backgroundSuccess: "#F4FFF4",

    // ===== SECONDARY COLORS (ORANGE/YELLOW) =====
    // Orange variations (darkest to lightest)
    textOrangeAccent: "#E67E22",
    backgroundAmber: "#D99C00",
    brandMediumOrange: "#d2d550",
    textWarningYellow: "#F1C40F",
    backgroundWarning: "#fff8ee",

    // Blue variations (darkest to lightest)
    textDarkBlue: "#00008b",
    textBlue: "#2c5282",
    infoSkyBlue: "#4299E1",
    backgroundLightCyan: "#f8f9fa",

    // Red variations (darkest to lightest)
    alertRed: "#FF0000",
    textDangerRed: "#e74c3c",
    textAlertRed: "#FF3B30",
    textRed: "#ff5349",

    // Other status colors (darkest to lightest)
    highlightLime: "#00FF00",

    // ===== CHART COLORS =====
    // Chart colors (darkest to lightest)
    chartTeal: "#00B8A9",
    chartBlue: "#5A92DB",
    chartSlateGray: "#737A83",
    chartOrange: "#EF6721",
    chartYellowOrange: "#EFAF25",
    chartCoralRed: "#FF6B6B",
    chartViolet: "#A259F7",

    // ===== UTILITY COLORS =====
    // Transparent and overlay colors
    transparent: "transparent",
  },
};

export const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        ...lightModePalette,
      },
    },
    dark: {
      palette: {
        ...darkModePalette,
      },
    },
  },
  typography: {
    fontSize: 14,
    fontWeightBold: 600,
    fontFamily: '"Mulish", sans-serif',
    h1: {
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h4: {
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h5: {
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h6: {
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle1: {
      fontSize: "1.075rem",
      fontWeight: 500,
      lineHeight: 1.2,
    },
    subtitle2: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    body1: {
      fontWeight: 500,
      lineHeight: 1.2,
    },
    body2: {
      fontWeight: 500,
      lineHeight: 1.2,
    },
    caption: {
      fontSize: "0.875rem",
      lineHeight: 1.2,
    },
    overline: {
      lineHeight: 1.3,
      fontWeight: 600,
    },
    button: {
      fontSize: "1.05rem",
      fontWeight: 600,
      lineHeight: 1.2,
    },
  },
  components: {
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
        disabled: true,
      },
    },

    MuiDialog: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          backgroundImage: "none",
        }),
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ArrowDownIcon,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ArrowDownIcon color="inherit" sx={{ fontSize: "1.25rem" }} />,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        color: "primary",
        size: "medium",
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          switch (ownerState.variant) {
            case "contained":
              return {
                borderRadius: "999px",
                textTransform: "none",
                "&.Mui-disabled": {
                  opacity: 1,
                  backgroundColor: theme.vars.palette.divider,
                  color: theme.vars.palette.text.disabled,
                },
              };
            case "outlined":
              return {
                borderRadius: "999px",
                textTransform: "none",
                "&.Mui-disabled": {
                  opacity: 1,
                  borderColor: theme.vars.palette.text.disabled,
                  color: theme.vars.palette.text.disabled,
                },
              };
            default:
              return;
          }
        },
        outlined: {
          borderWidth: 2,
          "&:hover": {
            borderWidth: 2,
          },
        },
        sizeSmall: {
          padding: ".3rem 0.8rem",
          minHeight: "2.25rem",
        },
        sizeMedium: {
          padding: ".35rem 1.2rem",
          minHeight: "3rem",
        },
        sizeLarge: {
          padding: ".4rem 1.4rem",
          minHeight: "3rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "0.875rem",
          overflow: "inherit",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
        },
        colorDefault: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: "none",
          border: `1px solid ${theme.vars.palette.grey[200]}`,
          backgroundColor: theme.vars.palette.background.paper,
          "&.Mui-disabled": {
            backgroundColor: `${theme.vars.palette.grey.A100} !important`,
            color: theme.vars.palette.text.disabled,
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: "none !important",
        },
        scroller: ({ theme }) => ({
          overflowY: "hidden",
          overflowX: "auto",
          borderBottom: `2px solid ${theme.vars.palette.grey[200]}`,
          position: "relative",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.grey[500],
          "&.Mui-selected": {
            color: theme.vars.palette.primary.main,
          },
          '[data-mui-color-scheme="dark"] &.Mui-selected': {
            color: theme.vars.palette.primary.light,
          },
        }),
      },
    },
  },
  shape: {
    borderRadius: 4,
  },
  shadows: [
    "none",
    "0px 4px 14px 0px rgba(0,0,0,0.14)",
    "0px 0px 16px 0px rgba(0,0,0,0.16)",
    "0px 0px 10px 0px rgba(0,0,0,0.4)",
    "0px 0px 20px 0px rgba(0,0,0,0.5)",
    "0px 3px 5px 0px rgba(0,0,0,0.2)",
    "0px 3px 5px 0px rgba(0,0,0,0.2)",
    "0px 4px 5px 0px rgba(0,0,0,0.2)",
    "0px 5px 5px 0px rgba(0,0,0,0.2)",
    "0px 5px 6px 0px rgba(0,0,0,0.2)",
    "0px 6px 6px 0px rgba(0,0,0,0.2)",
    "0px 6px 7px 0px rgba(0,0,0,0.2)",
    "0px 7px 8px 0px rgba(0,0,0,0.2)",
    "0px 7px 8px 0px rgba(0,0,0,0.2)",
    "0px 7px 9px 0px rgba(0,0,0,0.2)",
    "0px 8px 9px 0px rgba(0,0,0,0.2)",
    "0px 8px 10px 0px rgba(0,0,0,0.2)",
    "0px 8px 11px 0px rgba(0,0,0,0.2)",
    "0px 9px 11px 0px rgba(0,0,0,0.2)",
    "0px 9px 12px 0px rgba(0,0,0,0.2)",
    "0px 10px 13px 0px rgba(0,0,0,0.2)",
    "0px 10px 13px 0px rgba(0,0,0,0.2)",
    "0px 10px 14px 0px rgba(0,0,0,0.2)",
    "0px 11px 14px 0px rgba(0,0,0,0.2)",
    "0px 11px 15px 0px rgba(0,0,0,0.2))",
  ],
  spacing: 8,
});
